import React from 'react';
import cx from 'classnames';

import { usePatronSelector } from '../../../common/use-patron-selector';
import useScreenSize from '../../../common/use-screen-size';
import { contentSelectors } from '../../../models/content';
import { advocateSelectors } from '../../../models/advocate';
import { contentIsOwnedBy } from '../../../models/content/helpers';

import ContentActionBookmark from '../content-actions/content-action-bookmark';
import ContentActionComment from '../content-actions/content-action-comment';
import ContentActionEdit from '../content-actions/content-action-edit';
import ContentActionLike from '../content-actions/content-action-like';
import ContentActionShare from '../content-actions/content-action-share';
import { AcknowledgementFooter } from './acknowledgement-footer';
import ContentActionTranslateWrapper from '../content-actions/content-action-translate-wrapper';
import { ContentActionTranslate } from '../content-actions/content-action-translate';
import ContentActionViewLikes from '../content-actions/content-action-view-likes';
import ContentActionMore from '../content-actions/content-action-more/content-action-more';
import { FlagPostAction } from '../content-actions/content-action-more/content-action-flag-post';
import { ContentActionCopyLink } from '../content-actions/content-action-more/content-action-copy-link';
import ContentActionArchive from '../content-actions/content-action-archive';
import ContentActionDuplicate from '../content-actions/content-action-duplicate';
import { Feature } from '../../../models/features/features';
import useFeatureFlag from '../../../common/use-feature-flag';
import ContentActionPdf from '../content-actions/content-action-pdf';
import { useRouteMatch } from 'react-router';
import { ApprovalFooter } from './approval-footer';
import { programSelectors } from '../../../models/program';
import { FeedCardActionReact } from '../../content/solar-feed-card/feed-card-actions/action-like';
import { MobileReactionsFooter } from './mobile-reactions-footer';

interface InfoHeaderProps {
  contentId: number;
  analyticsData: { location: string };
  emailContent: boolean;
  isUgcPreview?: boolean;
}

export const InfoFooter: React.FC<InfoHeaderProps> = ({
  contentId,
  emailContent,
  analyticsData,
  isUgcPreview = false,
}) => {
  const isUgcV2 = usePatronSelector((state) =>
    contentSelectors.getContentIsUgcV2(state, { contentId })
  );
  const isDraft = usePatronSelector((state) =>
    contentSelectors.getContentIsDraft(state, { contentId })
  );
  const isArchived = usePatronSelector((state) =>
    contentSelectors.getContentIsArchived(state, { contentId })
  );
  const ugcEnabled = useFeatureFlag(Feature.UGC_ENABLED);

  const isUgc = ugcEnabled && (isUgcPreview || isUgcV2);

  const expandedReactionTypesEnabled = usePatronSelector((state) =>
    programSelectors.getExpandedReactionTypesEnabled(state)
  );
  const canLike = usePatronSelector((state) =>
    contentSelectors.getContentCanLike(state, { contentId })
  );
  const canComment = usePatronSelector((state) =>
    contentSelectors.getContentCanComment(state, { contentId })
  );
  const canBookmark = usePatronSelector((state) =>
    contentSelectors.getContentCanBookmark(state, { contentId })
  );
  const canShare = usePatronSelector((state) =>
    contentSelectors.getContentCanShare(state, { contentId })
  );
  const canEdit = usePatronSelector((state) =>
    contentSelectors.getContentCanEdit(state, { contentId })
  );
  const isDuplicatableInUgcEditor = usePatronSelector((state) =>
    contentSelectors.getContentIsDuplicatable(state, { contentId })
  );
  const canTranslate = usePatronSelector((state) =>
    contentSelectors.getContentCanTranslate(state, { contentId })
  );

  const acknowledgementActive = usePatronSelector((state) =>
    contentSelectors.getContentCanAcknowledge(state, { contentId })
  );
  const { isMobile } = useScreenSize();

  const canFlagPost = usePatronSelector((state) =>
    contentSelectors.getContentCanFlag(state, { contentId })
  );

  const advocateId = usePatronSelector((state) =>
    advocateSelectors.getAdvocateId(state)
  );
  const createdBy = usePatronSelector((state) =>
    contentSelectors.getContentCreatedBy(state, { contentId })
  );
  const isOwner = contentIsOwnedBy(createdBy, advocateId);

  const tescoQ3 = useFeatureFlag(Feature.TESCO_Q3);

  const footerContainerRef = React.useRef<HTMLDivElement>(null);

  const { path } = useRouteMatch();

  const isApproval = path.includes('approvals');

  // No editing after submission for UGC (decision journal Nov 10)
  const ugcEditLocked = isUgc && !isDraft;

  const isBookmarkInMoreAction = isMobile && canBookmark;
  const isShareInMoreAction = isMobile && canShare;
  const isCopyLinkInMoreAction = isMobile;
  const isFlagPostInMoreAction = canFlagPost;

  const moreActions = [
    isBookmarkInMoreAction ? (
      <ContentActionBookmark
        contentId={contentId}
        analyticsData={analyticsData}
        showText={true}
        wideMode={true}
        container={footerContainerRef.current}
      />
    ) : null,
    isShareInMoreAction ? (
      <ContentActionShare
        contentId={contentId}
        analyticsData={analyticsData}
        showText={true}
        wideMode={true}
        container={footerContainerRef.current}
      />
    ) : null,
    isCopyLinkInMoreAction ? (
      <ContentActionCopyLink
        contentId={contentId}
        analyticsData={analyticsData}
        wideMode={true}
        container={footerContainerRef.current}
      />
    ) : null,
    isFlagPostInMoreAction ? (
      <FlagPostAction contentId={contentId} analyticsData={analyticsData} />
    ) : null,
    isUgc && !isArchived && isOwner ? (
      <ContentActionArchive
        key="archive"
        contentId={contentId}
        wideMode={true}
        container={footerContainerRef.current}
      />
    ) : null,
    isUgc && isOwner ? (
      <ContentActionDuplicate
        key="duplicate"
        contentId={contentId}
        isDuplicatable={isDuplicatableInUgcEditor}
        wideMode={true}
        container={footerContainerRef.current}
      />
    ) : null,
  ].filter((el): el is JSX.Element => {
    return !!el;
  });

  return (
    <div
      ref={footerContainerRef}
      className={cx(
        'info-footer',
        expandedReactionTypesEnabled && 'expanded-reactions-enabled'
      )}
    >
      {isApproval && <ApprovalFooter contentId={contentId} />}
      {acknowledgementActive && <AcknowledgementFooter contentId={contentId} />}
      {expandedReactionTypesEnabled && isMobile && (
        <MobileReactionsFooter contentId={contentId} />
      )}
      {!emailContent ? (
        <div className="action-button-toolbar">
          <div className="info-footer__actions">
            <div className="info-footer__actions--left">
              {canLike ? (
                expandedReactionTypesEnabled ? (
                  <FeedCardActionReact
                    contentId={contentId}
                    analyticsData={analyticsData}
                    size="default"
                    container={footerContainerRef.current}
                  />
                ) : (
                  <ContentActionLike
                    contentId={contentId}
                    analyticsData={analyticsData}
                    showText={Boolean(!isMobile || tescoQ3)}
                  />
                )
              ) : null}

              {!isMobile && <ContentActionViewLikes contentId={contentId} />}
            </div>

            <div className="info-footer__actions--right">
              {canComment ? (
                <ContentActionComment
                  contentId={contentId}
                  analyticsData={analyticsData}
                  showText={!isMobile}
                  onClickAction="replace"
                  container={footerContainerRef.current}
                />
              ) : null}

              {canEdit && !ugcEditLocked ? (
                <ContentActionEdit
                  contentId={contentId}
                  analyticsData={analyticsData}
                  container={footerContainerRef.current}
                />
              ) : null}

              {canShare && !isMobile ? (
                <ContentActionShare
                  contentId={contentId}
                  analyticsData={analyticsData}
                  container={footerContainerRef.current}
                />
              ) : null}

              {canBookmark && !isMobile ? (
                <ContentActionBookmark
                  contentId={contentId}
                  analyticsData={analyticsData}
                  container={footerContainerRef.current}
                />
              ) : null}

              {canTranslate ? (
                <ContentActionTranslateWrapper
                  contentId={contentId}
                  analyticsData={analyticsData}
                >
                  {({ handleClick, isTranslated }) => (
                    <ContentActionTranslate
                      isTranslated={isTranslated}
                      handleClick={handleClick}
                      showText={false}
                      wideMode={false}
                      container={footerContainerRef.current}
                    />
                  )}
                </ContentActionTranslateWrapper>
              ) : null}

              {!isMobile && (
                <ContentActionPdf
                  contentId={contentId}
                  container={footerContainerRef.current}
                />
              )}

              {!isMobile && (
                <ContentActionCopyLink
                  contentId={contentId}
                  analyticsData={analyticsData}
                  wideMode={false}
                  container={footerContainerRef.current}
                />
              )}

              <ContentActionMore actions={moreActions} />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};
